import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useState, type CSSProperties } from "react";

import { sdk } from "@/api/sdk";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { languageName } from "@/lib/languages";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale, isLocale, localizeUrl, setLocale, type Locale } from "@/paraglide/runtime.js";
import { APP_LANGUAGES, DEFAULT_LANGUAGE, appLanguageOf, type AppLanguage } from "@complete-athlete/enums";

const withoutTrailingSlash = (pathname: string) => (pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname);

/**
 * Where the page the visitor is on lives in `language`, when that is not simply the same path under
 * the language's prefix (which is where `setLocale` goes by itself). A page whose languages are
 * separate addresses, such as a blog post with a slug per language, names them in its hreflang links.
 */
const ownAddressIn = (language: Locale): string | undefined => {
  // Electron has no addresses to carry a language: there the cookie is the whole choice.
  if (window.electron !== undefined) return undefined;
  const alternate = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${language}"]`);
  if (!alternate) return undefined;
  const own = new URL(alternate.href);
  const prefixed = localizeUrl(window.location.href, { locale: language });
  return withoutTrailingSlash(own.pathname) === withoutTrailingSlash(prefixed.pathname) ? undefined : own.href;
};

/**
 * Saved on the account first, so the next sign-in check does not switch the app back and the
 * emails follow. A visitor who is not signed in is answered 401 and the switch goes ahead: the
 * cookie and the address carry it (ADR 0026).
 */
const switchLanguage = async (language: AppLanguage) => {
  if (!isLocale(language)) return;
  await sdk.users.me.locale.$put.send({ payload: { locale: language } });
  const address = ownAddressIn(language);
  await setLocale(language, { reload: !address });
  if (address) window.location.assign(address);
};

/**
 * The language picker in a bar: a globe that opens every app language, named in the page's language. On
 * a public page (`bar`) it carries a chevron, as on Gravl; in the app's header (`icon`) it is one
 * more icon beside the bell. Settings keeps the full picker with `LanguageTabs`.
 *
 * The list renders in a portal, outside the page wrapper, so `style` carries the page's tints.
 */
export function LanguageMenu({
  variant = "bar",
  style,
  className,
}: {
  variant?: "bar" | "icon";
  style?: CSSProperties;
  className?: string;
}) {
  const current = appLanguageOf(getLocale()) ?? DEFAULT_LANGUAGE;
  const [switching, setSwitching] = useState(false);

  // The language already on screen is saved too, without a reload: a `/pl` link opened by someone
  // whose cookie says English shows Polish, and picking Polski there is what makes it stick.
  const pick = (value: string) => {
    const language = appLanguageOf(value);
    if (!language) return;
    if (language !== current) setSwitching(true);
    void switchLanguage(language);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        aria-label={m.language_menu_label()}
        disabled={switching}
        className={cn(
          "group inline-flex shrink-0 cursor-pointer items-center justify-center text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:text-foreground disabled:opacity-50",
          variant === "bar"
            ? "h-10 gap-1 rounded-full px-2.5 hover:bg-muted data-[state=open]:bg-muted"
            : "size-8 rounded-md hover:bg-accent data-[state=open]:bg-accent",
          className,
        )}
      >
        <Globe aria-hidden className={variant === "bar" ? "size-[18px]" : "size-4"} />
        {variant === "bar" ? (
          <ChevronDown
            aria-hidden
            className="size-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        ) : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} style={style} className="min-w-52 rounded-2xl p-1.5 shadow-xl">
        <DropdownMenuRadioGroup value={current} onValueChange={pick}>
          {APP_LANGUAGES.map((language) => (
            <DropdownMenuPrimitive.RadioItem
              key={language}
              value={language}
              className="flex h-10 cursor-pointer items-center gap-2 rounded-xl px-3 text-sm font-medium outline-none select-none focus:bg-accent focus:text-accent-foreground"
            >
              {languageName(language)}
              <DropdownMenuPrimitive.ItemIndicator className="ml-auto pl-3">
                <Check aria-hidden className="size-4" />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
